import { useEffect, useState, useRef, useCallback } from 'react'
import io, { Socket } from 'socket.io-client'

const SOCKET_URL = import.meta.env.VITE_ANTICHEAT_SOCKET_URL || 'https://cyberlete-production.herokuapp.com'

// How long to keep statuses before marking stale (5 minutes)
const STATUS_STALE_TIMEOUT = 5 * 60 * 1000
// How often to clean stale statuses (1 minute)
const CLEANUP_INTERVAL = 60 * 1000
// Reconnection config
const MAX_RECONNECT_ATTEMPTS = 10
const RECONNECT_DELAY = 2000

interface AnticheatStatusUpdate {
  user_id: string
  username: string
  avatar?: string | null
  status: 'active' | 'recording' | 'inactive' | 'offline'
  game_detected?: string | null
  is_recording?: boolean
  timestamp: Date
}

interface StatusEntry {
  status: string
  lastUpdated: number
}

export interface AnticheatSyncResult {
  statuses: Record<string, string>
  isConnected: boolean
  connectionError: string | null
  reconnectAttempts: number
}

/**
 * Hook to listen for real-time anticheat status updates from backend
 *
 * Returns an object with:
 * - statuses: map of user_id -> anticheat_status
 * - isConnected: whether socket is connected
 * - connectionError: any connection error message
 * - reconnectAttempts: number of reconnect attempts
 *
 * Features:
 * - Automatic stale status cleanup (marks users offline after 5min of no updates)
 * - Connection health monitoring
 * - Graceful reconnection handling
 * - Memory management (cleans up old entries)
 */
export const useAnticheatSync = (): AnticheatSyncResult => {
  const [statusEntries, setStatusEntries] = useState<Record<string, StatusEntry>>({})
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)

  const socketRef = useRef<Socket | null>(null)
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Clean up stale statuses
  const cleanupStaleStatuses = useCallback(() => {
    const now = Date.now()
    setStatusEntries((prev) => {
      const updated = { ...prev }
      let hasChanges = false

      for (const userId in updated) {
        const entry = updated[userId]
        // Mark as offline if no update received in STATUS_STALE_TIMEOUT
        if (now - entry.lastUpdated > STATUS_STALE_TIMEOUT && entry.status !== 'offline') {
          updated[userId] = { status: 'offline', lastUpdated: now }
          hasChanges = true
        }
        // Remove entries that have been offline for too long (double the timeout)
        if (now - entry.lastUpdated > STATUS_STALE_TIMEOUT * 2 && entry.status === 'offline') {
          delete updated[userId]
          hasChanges = true
        }
      }

      return hasChanges ? updated : prev
    })
  }, [])

  // Update a user's status
  const updateStatus = useCallback((userId: string, status: string) => {
    setStatusEntries((prev) => ({
      ...prev,
      [userId]: { status, lastUpdated: Date.now() },
    }))
  }, [])

  useEffect(() => {
    console.log('[AntiCheat Sync] Initializing Socket.IO connection...')

    // Connect to Socket.IO /anticheat namespace
    const socketInstance = io(`${SOCKET_URL}/anticheat`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: RECONNECT_DELAY,
      reconnectionDelayMax: RECONNECT_DELAY * 5,
      timeout: 10000,
    })

    socketRef.current = socketInstance

    socketInstance.on('connect', () => {
      console.log('[AntiCheat Sync] Connected to Socket.IO')
      setIsConnected(true)
      setConnectionError(null)
      setReconnectAttempts(0)
    })

    socketInstance.on('disconnect', (reason) => {
      console.log('[AntiCheat Sync] Disconnected:', reason)
      setIsConnected(false)
      if (reason === 'io server disconnect') {
        setConnectionError('Server closed the connection')
      }
    })

    socketInstance.on('connect_error', (error) => {
      console.error('[AntiCheat Sync] Connection error:', error.message)
      setConnectionError(error.message)
      setIsConnected(false)
    })

    socketInstance.on('reconnect_attempt', (attempt) => {
      console.log(`[AntiCheat Sync] Reconnect attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}`)
      setReconnectAttempts(attempt)
    })

    socketInstance.on('reconnect_failed', () => {
      console.error('[AntiCheat Sync] Reconnection failed after max attempts')
      setConnectionError('Unable to connect. Please refresh the page.')
    })

    socketInstance.on('reconnect', () => {
      console.log('[AntiCheat Sync] Reconnected successfully')
      setConnectionError(null)
      setReconnectAttempts(0)
    })

    // Listen for anticheat status updates
    socketInstance.on('anticheat_status_update', (data: AnticheatStatusUpdate) => {
      if (data.user_id) {
        updateStatus(data.user_id, data.status)
      }
    })

    // Listen for desktop connected events
    socketInstance.on('desktop_connected', (data: { user_id: string; username: string }) => {
      if (data.user_id) {
        updateStatus(data.user_id, 'active')
      }
    })

    // Listen for desktop disconnected events
    socketInstance.on('desktop_disconnected', (data: { user_id: string; username: string }) => {
      if (data.user_id) {
        updateStatus(data.user_id, 'offline')
      }
    })

    // Start cleanup interval
    cleanupIntervalRef.current = setInterval(cleanupStaleStatuses, CLEANUP_INTERVAL)

    // Cleanup on unmount
    return () => {
      console.log('[AntiCheat Sync] Cleaning up Socket.IO connection')

      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current)
      }

      socketInstance.off('anticheat_status_update')
      socketInstance.off('desktop_connected')
      socketInstance.off('desktop_disconnected')
      socketInstance.off('connect')
      socketInstance.off('disconnect')
      socketInstance.off('connect_error')
      socketInstance.off('reconnect_attempt')
      socketInstance.off('reconnect_failed')
      socketInstance.off('reconnect')
      socketInstance.disconnect()
      socketRef.current = null
    }
  }, [updateStatus, cleanupStaleStatuses])

  // Convert statusEntries to simple status map for backward compatibility
  const statuses = Object.fromEntries(
    Object.entries(statusEntries).map(([userId, entry]) => [userId, entry.status])
  )

  return {
    statuses,
    isConnected,
    connectionError,
    reconnectAttempts,
  }
}

/**
 * Simple hook that returns just the statuses map (backward compatible)
 */
export const useAnticheatStatuses = (): Record<string, string> => {
  const { statuses } = useAnticheatSync()
  return statuses
}
